import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Lien invalide ou expiré.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success(res.data.message || "Mot de passe réinitialisé");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      const msg = err.response?.data?.detail || "Une erreur s'est produite";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Mot de passe modifié ✓</h2>
            <p className="text-sm text-slate-500 mt-3">Redirection vers la page de connexion...</p>
          </div>
        ) : error && !token ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Lien invalide</h2>
            <p className="text-sm text-slate-500 mt-3">{error}</p>
            <Link to="/forgot-password">
              <Button className="mt-6 bg-sky-600 hover:bg-sky-500 text-white">Demander un nouveau lien</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-sky-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Nouveau mot de passe</h1>
              <p className="text-sm text-slate-500 mt-2">Choisissez un mot de passe sécurisé (6 caractères minimum)</p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    autoFocus
                    minLength={6}
                    data-testid="new-password-input"
                    className="w-full px-4 py-2.5 pr-11 border border-slate-200 rounded-lg text-slate-800 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition"
                  />
                  <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Confirmer le mot de passe</label>
                <input
                  type={showPwd ? "text" : "password"}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-slate-800 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !pwd || !pwd2}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold py-2.5 gap-2"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Réinitialiser mon mot de passe
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
